import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ContentPasteOutlinedIcon from "@mui/icons-material/ContentPasteOutlined";
import LocalPrintshopOutlinedIcon from "@mui/icons-material/LocalPrintshopOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  createPrescriptionTemplate,
  deletePrescriptionTemplate,
  listPrescriptionTemplates,
} from "../../services/prescriptionTemplates.service";
import { IPrescriptionTemplate, TemplateFieldType } from "../../interfaces/IPrescriptionTemplate";
import { generateClinicalDocumentPDF } from "../../utils/generateClinicalDocumentPDF";

const TEMPLATE_MENU_LIMIT = 5;

interface FieldTemplateControlsProps {
  fieldType: TemplateFieldType;
  value: string;
  onApply: (content: string) => void;
  /** false quando o campo não está em modo de edição — esconde os controles de modelo */
  editable: boolean;
  isDoctor: boolean;

  canPrint: boolean;
  printLabel: string;
  printTitle: string;
  printFileSlug: string;
  printUppercase?: boolean;
  patientName?: string | null;
  patientCPF?: string | null;
  doctorName?: string | null;
  doctorCRM?: string | null;
  date?: string | null;
}

export function FieldTemplateControls({
  fieldType,
  value,
  onApply,
  editable,
  isDoctor,
  canPrint,
  printLabel,
  printTitle,
  printFileSlug,
  printUppercase,
  patientName,
  patientCPF,
  doctorName,
  doctorCRM,
  date,
}: FieldTemplateControlsProps) {
  const [templates, setTemplates] = useState<IPrescriptionTemplate[]>([]);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [selectAllOpen, setSelectAllOpen] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [printing, setPrinting] = useState(false);

  useEffect(() => {
    if (!isDoctor) return;
    let ignore = false;
    listPrescriptionTemplates(fieldType).then((data) => {
      if (!ignore) setTemplates(data);
    }).catch(() => undefined);
    return () => { ignore = true; };
  }, [isDoctor, fieldType]);

  const handleApply = (tpl: IPrescriptionTemplate) => {
    onApply(tpl.content);
    setMenuAnchor(null);
  };

  const handleOpenSave = () => {
    setMenuAnchor(null);
    setNewTitle("");
    setSaveOpen(true);
  };

  const handleConfirmSave = async () => {
    if (!newTitle.trim() || !value.trim()) return;
    setSaving(true);
    try {
      const created = await createPrescriptionTemplate({ type: fieldType, title: newTitle.trim(), content: value });
      setTemplates((prev) => [created, ...prev]);
      toast.success("Modelo salvo com sucesso.");
      setSaveOpen(false);
    } catch {
      toast.error("Não foi possível salvar o modelo.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    try {
      await deletePrescriptionTemplate(id);
      setTemplates((prev) => prev.filter((t) => t.id !== id));
      toast.success("Modelo excluído.");
    } catch {
      toast.error("Não foi possível excluir o modelo.");
    } finally {
      setDeletingId(null);
    }
  };

  const handlePrint = async () => {
    setPrinting(true);
    try {
      await generateClinicalDocumentPDF({
        title: printTitle,
        content: value,
        uppercase: printUppercase,
        fileSlug: printFileSlug,
        patientName,
        patientCPF,
        doctorName,
        doctorCRM,
        date,
      });
    } catch {
      toast.error("Não foi possível gerar o PDF.");
    } finally {
      setPrinting(false);
    }
  };

  return (
    <>
      <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: 1 }}>
        {editable && isDoctor && (
          <>
            <Button
              size="small"
              startIcon={<ContentPasteOutlinedIcon />}
              onClick={(e) => setMenuAnchor(e.currentTarget)}
              sx={{ color: "#133859" }}
            >
              Usar modelo
            </Button>
            <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
              {templates.length === 0 && (
                <MenuItem disabled>Nenhum modelo salvo</MenuItem>
              )}
              {templates.slice(0, TEMPLATE_MENU_LIMIT).map((tpl) => (
                <MenuItem key={tpl.id} onClick={() => handleApply(tpl)}>
                  {tpl.title}
                </MenuItem>
              ))}
              {templates.length > TEMPLATE_MENU_LIMIT && (
                <MenuItem onClick={() => { setMenuAnchor(null); setSelectAllOpen(true); }}>
                  Ver todos ({templates.length})
                </MenuItem>
              )}
              <Divider />
              <MenuItem onClick={handleOpenSave} disabled={!value.trim()}>
                Salvar prescrição atual como modelo
              </MenuItem>
              <MenuItem onClick={() => { setMenuAnchor(null); setManageOpen(true); }}>
                Gerenciar modelos
              </MenuItem>
            </Menu>
          </>
        )}
        {canPrint && (
          <Button
            startIcon={printing ? <CircularProgress size={14} /> : <LocalPrintshopOutlinedIcon />}
            variant="outlined"
            size="small"
            onClick={handlePrint}
            disabled={printing}
            sx={{ borderColor: "#133859", color: "#133859" }}
          >
            {printing ? "Gerando..." : printLabel}
          </Button>
        )}
      </Stack>

      {/* ── Dialog: ver todos os modelos (seleção) ─────────────────────────── */}
      <Dialog open={selectAllOpen} onClose={() => setSelectAllOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Meus modelos</DialogTitle>
        <DialogContent>
          {templates.length === 0 ? (
            <Typography variant="body2" color="text.secondary">Nenhum modelo salvo ainda.</Typography>
          ) : (
            <List dense>
              {templates.map((tpl) => (
                <ListItem key={tpl.id} disablePadding>
                  <ListItemButton
                    onClick={() => {
                      handleApply(tpl);
                      setSelectAllOpen(false);
                    }}
                  >
                    <ListItemText primary={tpl.title} secondary={tpl.content} secondaryTypographyProps={{ noWrap: true }} />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectAllOpen(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog: salvar como modelo ──────────────────────────────────────── */}
      <Dialog open={saveOpen} onClose={() => setSaveOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Salvar como modelo</DialogTitle>
        <DialogContent>
          <TextField autoFocus fullWidth label="Nome do modelo" value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)} sx={{ mt: 1 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveOpen(false)} disabled={saving}>Cancelar</Button>
          <Button variant="contained" onClick={handleConfirmSave} disabled={saving || !newTitle.trim()} sx={{ backgroundColor: "#133859" }}>
            {saving ? "Salvando..." : "Salvar"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog: gerenciar modelos ──────────────────────────────────────── */}
      <Dialog open={manageOpen} onClose={() => setManageOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Meus modelos</DialogTitle>
        <DialogContent>
          {templates.length === 0 ? (
            <Typography variant="body2" color="text.secondary">Nenhum modelo salvo ainda.</Typography>
          ) : (
            <List dense>
              {templates.map((tpl) => (
                <ListItem
                  key={tpl.id}
                  secondaryAction={
                    <IconButton edge="end" size="small" onClick={() => handleDelete(tpl.id)} disabled={deletingId === tpl.id}>
                      {deletingId === tpl.id ? <CircularProgress size={16} /> : <DeleteOutlineIcon fontSize="small" />}
                    </IconButton>
                  }
                >
                  <ListItemText primary={tpl.title} secondary={tpl.content} secondaryTypographyProps={{ noWrap: true }} />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManageOpen(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
